import React from 'react'
import { useRouter } from 'next/router'
import { MailOutline, PhoneOutlined } from '@mui/icons-material'
import { Box, Flex, Text } from '@fundamentals'
import { Card, Link } from '@common/material'
import { CopyToClipboardButton } from '@common/CopyToClipboardButton'
import { useOrganisationUser } from '@core/react-query/features/organisations/organisation/hooks/useOrganisationUser'
import { useProjectUser } from '@core/react-query/features/projects/project/hooks/useProjectUser'
import {
  OrganisationUser,
  OrganisationRole,
  ProjectUser,
  ProjectRole,
} from '@core/entities/user'
import { PageUrls } from '@core/page-urls'
import { localizedStrings } from '@core/strings'
import { UserCardHeader } from './components/UserCardHeader'
import { UserCardBanner } from './components/UserCardBanner'
import { LastBriefedRow } from './components/LastBriefedRow'

const BADGE_ORG_ROLES: OrganisationRole[] = ['ACCOUNT_OWNER', 'ACCOUNT_VIEWER']
const BADGE_PROJECT_ROLES: ProjectRole[] = ['PROJECT_ADMIN', 'PROJECT_VIEWER']

type UserProfileCardProps = {
  userId: number
  organisationId: number
  projectId?: number
  lastBriefedOn?: string
}

export const UserProfileCard: React.FC<UserProfileCardProps> = ({
  userId,
  organisationId,
  projectId,
  lastBriefedOn,
}) => {
  const router = useRouter()

  const { data: user, isLoading } = useOrganisationUser({
    organisationId,
    userId,
  })
  const { data: projectUser } = useProjectUser(
    { projectId, userId },
    { enabled: !!projectId },
  )

  if (isLoading || !user) {
    return (
      <Card
        sx={{
          minWidth: 280,
          width: 'max-content',
          p: 1.5,
          boxShadow: '0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
        }}
      >
        <Text fontSize={14} color='text.secondary'>
          {localizedStrings.loading}
        </Text>
      </Card>
    )
  }

  const orgBadgeRole = BADGE_ORG_ROLES.find((role) =>
    user?.roles?.includes(role),
  )
  const projectBadgeRole = BADGE_PROJECT_ROLES.find((role) =>
    projectUser?.roles?.includes(role),
  )
  let badgeLabel: string | null = null
  if (orgBadgeRole) {
    badgeLabel = OrganisationUser.roleString(orgBadgeRole)
  } else if (projectBadgeRole) {
    badgeLabel = ProjectUser.getRoleString(projectBadgeRole)
  }

  return (
    <Card
      sx={{
        minWidth: 280,
        width: 'max-content',
        overflow: 'hidden',
        boxShadow: '0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
      }}
    >
      {(!user?.isActive() || projectUser?.isDisabled()) && (
        <UserCardBanner>{localizedStrings.userIsDeactivated}</UserCardBanner>
      )}

      <Box>
        <UserCardHeader
          user={user}
          name={user.getFullName()}
          badgeLabel={badgeLabel}
          onClick={(e) => {
            e.stopPropagation()
            router.push(
              PageUrls.organisationUserProfile({
                organisationId,
                userId,
              }),
            )
          }}
        />

        <Flex flexDirection='column' gap={1} px={1.5} pb={1.5}>
          {user.email && (
            <Flex
              alignItems='center'
              gap={1}
              sx={{
                minWidth: 0,
                '&:hover .copy-clipboard-btn': { opacity: 0.6 },
              }}
            >
              <MailOutline
                sx={{
                  fontSize: 16,
                  color: 'text.secondary',
                  flexShrink: 0,
                }}
              />
              <Link
                href={`mailto:${user.email}`}
                underline='none'
                onClick={(e) => e.stopPropagation()}
                sx={{
                  color: 'text.primary',
                  minWidth: 0,
                  textDecoration: 'none',
                  '&:hover': {
                    color: 'primary.main',
                    textDecoration: 'none',
                  },
                }}
              >
                <Text fontSize={12} noWrap>
                  {user.email}
                </Text>
              </Link>
              <CopyToClipboardButton value={user.email} />
            </Flex>
          )}

          {user.phone && (
            <Flex
              alignItems='center'
              gap={1}
              sx={{
                minWidth: 0,
                '&:hover .copy-clipboard-btn': { opacity: 0.6 },
              }}
            >
              <PhoneOutlined
                sx={{
                  fontSize: 16,
                  color: 'text.secondary',
                  flexShrink: 0,
                }}
              />
              <Link
                href={`tel:${user.phone}`}
                underline='none'
                onClick={(e) => e.stopPropagation()}
                sx={{
                  color: 'text.primary',
                  minWidth: 0,
                  textDecoration: 'none',
                  '&:hover': {
                    color: 'primary.main',
                    textDecoration: 'none',
                  },
                }}
              >
                <Text fontSize={12} noWrap>
                  {user.phone}
                </Text>
              </Link>
              <CopyToClipboardButton value={user.phone} />
            </Flex>
          )}

          {lastBriefedOn && <LastBriefedRow lastBriefedOn={lastBriefedOn} />}
        </Flex>
      </Box>

      <Box px={1.5} pb={1.5}>
        <Text fontSize={12} color='text.secondary'>
          {`${localizedStrings.lastActive} ${user.getLastActiveString()}`}
        </Text>
      </Box>
    </Card>
  )
}
